import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import {
  CMasthead,
  CMastheadFragments,
  CMastheadBlock,
  CSeomatic,
  SeomaticData,
  CImageCopyBlocksFragments,
  CImageCopyBlocksBlock,
  CImageCopyBlocks,
  CFindADoctor,
  CCenteredCopy,
  CWtwSlides,
  CWtwSlidesFragments,
  CWtwSlidesBlock,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { gql } from '@urql/core'
import { getSiteInfo, serverSideValidateWww } from 'shared'
import { defaultClient } from '../graphql/clients'
import fragments from '../helpers/gql-fragments'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_worthTheWait_Entry {
            masthead {
              ...masthead
            }
            wtwSlides {
              ...wtwSlides
            }
            imageCopyBlocks {
              ...imageCopyBlocks
            }
          }
        }
      }
      ${CMastheadFragments.field}
      ${CWtwSlidesFragments.field}
      ${CImageCopyBlocksFragments.field}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
    },
  }
}

type Props = {
  entry: {
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    wtwSlides: Array<CWtwSlidesBlock>
    imageCopyBlocks: Array<CImageCopyBlocksBlock>
  }
  global: CraftGlobals
}

export default function WothTheWait({ entry, global }: Props) {
  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      <CMasthead field={entry.masthead} />
      <CWtwSlides field={entry.wtwSlides} />
      <CImageCopyBlocks field={entry.imageCopyBlocks} />
      <CFindADoctor field={global.findADoctor} />
      <CCenteredCopy field={global.centeredCopy} />
    </DefaultLayout>
  )
}
